import React, { useState } from "react";
import axios from "axios";
import { Send, MapPin, Mail, Loader2, CheckCircle, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    inquiry_type: "general",
  });
  const [status, setStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: "", message: "" });

    try {
      const response = await axios.post(`${API}/contact`, formData);
      setStatus({ 
        type: "success", 
        message: response.data.message || "Thank you for your message! We'll get back to you soon." 
      });
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
        inquiry_type: "general",
      });
    } catch (error) {
      setStatus({ 
        type: "error", 
        message: error.response?.data?.detail || "Something went wrong. Please try again." 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inquiryTypes = [
    { value: "general", label: "General Inquiry" },
    { value: "wholesale", label: "Wholesale / Distribution" },
    { value: "partnership", label: "Partnership Opportunity" },
    { value: "press", label: "Press / Media" },
    { value: "other", label: "Other" },
  ];

  return (
    <div className="bg-[#050505] pt-20" data-testid="contact-page">
      {/* Hero */}
      <section className="py-24 sm:py-32">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto">
            <p className="font-subheading text-xs tracking-[0.3em] uppercase text-[#C8A876] mb-4">
              Get in Touch
            </p>
            <h1 className="font-heading text-5xl sm:text-6xl tracking-tighter text-white mb-6">
              Contact Us
            </h1>
            <p className="text-[#A3A3A3] text-lg">
              Have questions about our products, interested in partnership opportunities, 
              or want to bring 8eight8 to your area? We'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="pb-24 sm:pb-32">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div className="order-2 lg:order-1">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                {/* Inquiry Type */}
                <div>
                  <label className="block text-sm text-[#A3A3A3] mb-2">
                    Inquiry Type
                  </label>
                  <select
                    name="inquiry_type"
                    value={formData.inquiry_type}
                    onChange={handleChange}
                    className="w-full bg-[#0A0A0A] border border-white/10 text-white p-4 focus:border-[#C8A876] transition-colors"
                    data-testid="inquiry-type-select"
                  >
                    {inquiryTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Name */}
                <div>
                  <label className="block text-sm text-[#A3A3A3] mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your name"
                    className="w-full"
                    data-testid="contact-name-input"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm text-[#A3A3A3] mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your@email.com"
                    className="w-full"
                    data-testid="contact-email-input"
                  />
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-sm text-[#A3A3A3] mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    placeholder="What is this regarding?"
                    className="w-full"
                    data-testid="contact-subject-input"
                  />
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm text-[#A3A3A3] mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    placeholder="Tell us more..."
                    className="w-full resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                {/* Status Message */}
                {status.message && (
                  <div 
                    className={`flex items-center gap-3 p-4 ${
                      status.type === "success" 
                        ? "bg-green-900/20 border border-green-500/30 text-green-400" 
                        : "bg-red-900/20 border border-red-500/30 text-red-400"
                    }`}
                    data-testid="contact-status-message"
                  >
                    {status.type === "success" ? (
                      <CheckCircle size={20} />
                    ) : (
                      <AlertCircle size={20} />
                    )}
                    <span>{status.message}</span>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-maduro w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="order-1 lg:order-2">
              <div className="sticky top-32">
                <h2 className="font-heading text-3xl text-white mb-8">
                  Let's Connect
                </h2>
                <p className="text-[#A3A3A3] mb-12 leading-relaxed">
                  Whether you're a retailer looking to stock our products, a media outlet 
                  seeking information, or simply a fan wanting to learn more—we're here for you.
                </p>

                <div className="space-y-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 border border-[#C8A876] flex items-center justify-center flex-shrink-0">
                      <Mail size={20} className="text-[#C8A876]" />
                    </div>
                    <div>
                      <h3 className="text-white font-medium mb-1">Email Us</h3>
                      <p className="text-[#A3A3A3]">everything@8eight8.net</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 border border-[#C8A876] flex items-center justify-center flex-shrink-0">
                      <MapPin size={20} className="text-[#C8A876]" />
                    </div>
                    <div>
                      <h3 className="text-white font-medium mb-1">Location</h3>
                      <p className="text-[#A3A3A3]">8eight8 Beverage Co LLC<br />United States</p>
                    </div>
                  </div>
                </div>

                {/* Response Time */}
                <div className="mt-12 p-6 bg-[#0A0A0A] border border-white/5">
                  <h3 className="text-white font-medium mb-2">Response Time</h3>
                  <p className="text-[#A3A3A3] text-sm">
                    We typically respond within 1-2 business days. For urgent wholesale 
                    inquiries, please indicate in your subject line.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
